import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { t } from 'i18next';
import i18n from '../../../language/i18n';

import { getUserInfo } from '../../../api/usercall';
import { postPromote } from '../../../api/postPromote';
import { getPromotionInfo } from '../../../api/getPromotionInfo';
import { getPromotionPackages } from '../../../api/getPromotionPackages';

import min_icon from '../../../assets/icons/min_flash.svg';
import max_icon from '../../../assets/icons/max_flash.svg';
import opt_icon from '../../../assets/icons/opt_flash.svg';
import phoneIcon from '../../../assets/icons/phone.svg';
import eyeIcon from '../../../assets/icons/eye.svg';
import heartIcon from '../../../assets/icons/heart.svg';
import chatIcon from '../../../assets/icons/chat.svg';

const PromoteContent = ({ data }) => {
    const [userBalance, setUserBalance] = useState(0);
    const [activatedServices, setActivatedServices] = useState({});
    const [promotionInfo, setPromotionInfo] = useState(null);
    const [isLoadingPromotion, setIsLoadingPromotion] = useState(true);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [promotionPackages, setPromotionPackages] = useState([]);
    const [isLoadingPackages, setIsLoadingPackages] = useState(true);

    const formatTimeRemaining = (seconds) => {
        if (seconds === null || seconds === undefined || seconds <= 0) {
            return t('unlimited') || 'Шексіз';
        }
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        if (days > 0) return `${days} ${t('days') || 'күн'}`;
        if (hours > 0) return `${hours} ${t('hours') || 'сағат'}`;
        return `${minutes} ${t('minutes') || 'минут'}`;
    };

    useEffect(() => {
        if (!promotionInfo?.remaining_time_seconds) return;
        setTimeRemaining(promotionInfo.remaining_time_seconds);
        const interval = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [promotionInfo]);

    const getApiLanguageCode = () => {
        const currentLang = i18n.language || 'kz';
        return currentLang === 'kz' ? 'kk' : currentLang;
    };

    const getLocalizedField = (packageItem, fieldName) => {
        const lang = getApiLanguageCode();
        const fieldKey = `${fieldName}_${lang}`;
        return packageItem[fieldKey] || packageItem[fieldName] || '';
    };

    const getPromotionTypeIndex = (packageId) => {
        const mapping = { minimal: 0, optimal: 1, maximal: 2 };
        return mapping[packageId] ?? 0;
    };

    const getPackageIcon = (packageId) => {
        const icons = { minimal: min_icon, optimal: opt_icon, maximal: max_icon };
        return icons[packageId] || min_icon;
    };

    useEffect(() => {
        const fetchUserBalance = async () => {
            try {
                const response = await getUserInfo();
                setUserBalance(response.user.balance);
            } catch (error) {
                console.error('Failed to fetch user balance:', error);
            }
        };
        fetchUserBalance();
    }, []);

    useEffect(() => {
        const fetchPromotionPackages = async () => {
            setIsLoadingPackages(true);
            try {
                const packages = await getPromotionPackages();
                setPromotionPackages(packages.filter((pkg) => pkg.is_active));
            } catch (error) {
                console.error('Failed to fetch promotion packages:', error);
                setPromotionPackages([]);
            } finally {
                setIsLoadingPackages(false);
            }
        };
        fetchPromotionPackages();
    }, []);

    useEffect(() => {
        const fetchPromotionInfo = async () => {
            if (!data?.id) return;
            setIsLoadingPromotion(true);
            try {
                const response = await getPromotionInfo(data.id);
                let info = null;
                if (response?.promotion) info = response.promotion;
                else if (response?.id || response?.package_type) info = response;
                setPromotionInfo(info);
            } catch (error) {
                console.error('Failed to fetch promotion info:', error);
                setPromotionInfo(null);
            } finally {
                setIsLoadingPromotion(false);
            }
        };
        fetchPromotionInfo();
    }, [data?.id]);

    const handleActivateClick = async (id, item) => {
        if (promotionInfo?.status === 'active' || promotionInfo?.status === 'pending') {
            const message =
                promotionInfo.status === 'active'
                    ? t('activePromotionExists') || 'Жарнамада белсенді промо бар'
                    : t('promotionPending') || 'Промо белсендіру процесінде';
            alert(message);
            return;
        }

        if (userBalance < item.units) {
            alert(t('notEnoughUnits') || 'Баланста бірлік жеткіліксіз');
            return;
        }

        try {
            const response = await postPromote(id, item.promotionType);
            if (response.promotion_id) {
                setActivatedServices((prev) => ({ ...prev, [item.name]: true }));
                setUserBalance((prev) => prev - item.units);
                const updatedInfo = await getPromotionInfo(id);
                setPromotionInfo(updatedInfo);
                alert(t('promotionActivated') || 'Тариф сәтті белсендірілді');
            } else {
                alert(t('activationFailed') || 'Белсендіру мүмкін болмады');
            }
        } catch (error) {
            console.error('Activation error:', error);
            alert(error.message || t('activationError') || 'Қате орын алды');
        }
    };

    const getPackageName = (type) => {
        const names = {
            minimal: t('minimalTitle') || 'Минимал',
            optimal: t('optimalTitle') || 'Оптимал',
            maximal: t('maximalTitle') || 'Максимал',
        };
        return names[type] || type || 'Белгісіз';
    };

    const getStatusText = (status) => {
        const texts = {
            pending: t('promotionStatusPending') || 'Күтуде',
            active: t('promotionStatusActive') || 'Белсенді',
            expired: t('promotionStatusExpired') || 'Бітті',
        };
        return texts[status] || status || 'Белгісіз';
    };

    const StatItem = ({ icon: Icon, value, label }) => (
        <div className="flex items-center gap-2 text-gray-600">
            <img src={Icon} alt="" className="w-5 h-5 opacity-60" />
            <span className="font-semibold text-gray-900">{value}</span>
            <span className="text-sm text-gray-500 hidden sm:inline">{label}</span>
        </div>
    );

    return (
        <div className="py-6">
                {/* Header - Product Info */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
                    <div className="p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                            {/* Product Image */}
                            <div className="flex-shrink-0">
                                <img
                                    src={data.main_image_url || '/placeholder-product.jpg'}
                                    alt={data.title}
                                    className="w-full sm:w-32 sm:h-32 md:w-40 md:h-40 object-cover rounded-xl bg-gray-100"
                                />
                            </div>

                            {/* Product Details */}
                            <div className="flex-1 min-w-0">
                                <h1 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                                    {data.title}
                                </h1>
                                <p className="text-2xl sm:text-3xl font-bold text-green-600 mb-4">
                                    {data.price} {t('currency') || '₸'}
                                </p>

                                {/* Stats */}
                                <div className="flex flex-wrap gap-4 sm:gap-6">
                                    <StatItem icon={phoneIcon} value={data.calls_count || 0} label={t('calls') || 'қоңырау'} />
                                    <StatItem icon={eyeIcon} value={data.views_count || 0} label={t('views') || 'көрілу'} />
                                    <StatItem icon={heartIcon} value={data.favorites_count || 0} label={t('favorites') || 'ұнату'} />
                                    <StatItem icon={chatIcon} value={data.messages_count || 0} label={t('messages') || 'хабарлама'} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Balance & Current Promotion */}
                    <div className="lg:col-span-1 space-y-6">
                        {/* Balance Card */}
                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-lg p-6 text-white">
                            <p className="text-green-100 text-sm mb-1">{t('yourBalance') || 'Баланс'}</p>
                            <p className="text-3xl sm:text-4xl font-bold mb-4">
                                {userBalance} <span className="text-xl font-medium">{t('units') || 'бир.'}</span>
                            </p>
                            <Link
                                to="/profile#walletTopUp"
                                className="inline-flex items-center justify-center w-full bg-white text-green-600 font-semibold py-3 px-4 rounded-xl hover:bg-green-50 transition-colors"
                            >
                                + {t('topupBalance') || 'Толықтыру'}
                            </Link>
                        </div>

                        {/* Current Promotion Info */}
                        {isLoadingPromotion ? (
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                                <div className="animate-pulse space-y-3">
                                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                                </div>
                            </div>
                        ) : promotionInfo ? (
                            <div className={`rounded-2xl shadow-sm border-2 p-6 ${
                                promotionInfo.status === 'active'
                                    ? 'bg-green-50 border-green-200'
                                    : promotionInfo.status === 'pending'
                                    ? 'bg-yellow-50 border-yellow-200'
                                    : 'bg-gray-50 border-gray-200'
                            }`}>
                                <div className="flex items-center gap-2 mb-4">
                                    <span className={`w-3 h-3 rounded-full ${
                                        promotionInfo.status === 'active' ? 'bg-green-500' :
                                        promotionInfo.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-400'
                                    }`} />
                                    <h3 className="font-bold text-gray-900">
                                        {t('currentPromotion') || 'Ағымдағы промо'}
                                    </h3>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-600">{t('package') || 'Пакет'}</span>
                                        <span className="font-semibold">{getPackageName(promotionInfo.package_type)}</span>
                                    </div>

                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-600">{t('status') || 'Статус'}</span>
                                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                                            promotionInfo.status === 'active' ? 'bg-green-100 text-green-800' :
                                            promotionInfo.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-gray-100 text-gray-800'
                                        }`}>
                                            {getStatusText(promotionInfo.status)}
                                        </span>
                                    </div>

                                    {/* Views Info - show for active and pending */}
                                    <div>
                                        <div className="flex justify-between text-sm mb-2">
                                            <span className="text-gray-600">{t('viewsProgress') || 'Прогресс'}</span>
                                            <span className="font-medium">{promotionInfo.progress_percentage || 0}%</span>
                                        </div>
                                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all duration-500 ${
                                                    promotionInfo.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'
                                                }`}
                                                style={{ width: `${promotionInfo.progress_percentage || 0}%` }}
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {promotionInfo.current_views || 0} / {promotionInfo.max_views || 0} {t('views') || 'көрілу'}
                                        </p>
                                    </div>

                                    {/* Remaining Views */}
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-600">{t('remainingViews') || 'Қалған көрілу'}</span>
                                        <span className={`font-semibold ${
                                            promotionInfo.status === 'active' ? 'text-green-600' : 'text-yellow-600'
                                        }`}>
                                            {promotionInfo.remaining_views || 0}
                                        </span>
                                    </div>

                                    {/* Time Remaining - only for active */}
                                    {promotionInfo.status === 'active' && timeRemaining !== null && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-600">{t('timeRemaining') || 'Қалған уақыт'}</span>
                                            <span className="font-semibold text-green-600">
                                                {formatTimeRemaining(timeRemaining)}
                                            </span>
                                        </div>
                                    )}

                                    {/* Created Date */}
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-600">{t('createdAt') || 'Құрылды'}</span>
                                        <span className="font-semibold text-gray-700">
                                            {promotionInfo.created_at
                                                ? new Date(promotionInfo.created_at).toLocaleDateString('kk-KZ')
                                                : '-'
                                            }
                                        </span>
                                    </div>

                                    {/* Expires At - if available */}
                                    {promotionInfo.expires_at && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-600">{t('expiresAt') || 'Аяқталады'}</span>
                                            <span className="font-semibold text-gray-700">
                                                {new Date(promotionInfo.expires_at).toLocaleDateString('kk-KZ')}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {promotionInfo.status === 'active' && (
                                    <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded-xl text-sm text-yellow-800">
                                        {t('activePromotionWarning') || 'Белсенді промо бар'}
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>

                    {/* Right Column - Promotion Packages */}
                    <div className="lg:col-span-2">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">
                            {t('promoteTitle') || 'Жарнаманы ілгерілету'}
                        </h2>

                        {isLoadingPackages ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                                        <div className="h-12 w-12 bg-gray-200 rounded-xl mb-4" />
                                        <div className="h-6 bg-gray-200 rounded mb-2" />
                                        <div className="h-4 bg-gray-200 rounded w-2/3" />
                                    </div>
                                ))}
                            </div>
                        ) : promotionPackages.length === 0 ? (
                            <div className="bg-white rounded-2xl p-8 text-center text-gray-500">
                                {t('noData') || 'Деректер жоқ'}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {promotionPackages.map((packageItem) => {
                                    const service = {
                                        id: packageItem.id,
                                        name: getLocalizedField(packageItem, 'title'),
                                        icon: getPackageIcon(packageItem.id),
                                        units: packageItem.units,
                                        price: packageItem.price,
                                        currency: packageItem.currency || '₸',
                                        promotionType: getPromotionTypeIndex(packageItem.id),
                                        duration: getLocalizedField(packageItem, 'duration_description'),
                                    };

                                    const isActive = activatedServices[service.name];
                                    const hasActivePromotion = promotionInfo?.status === 'active' || promotionInfo?.status === 'pending';
                                    const canAfford = userBalance >= service.units;

                                    return (
                                        <div
                                            key={service.id}
                                            className={`bg-white rounded-2xl shadow-sm border-2 p-5 flex flex-col transition-all hover:shadow-md ${
                                                isActive ? 'border-green-500 bg-green-50' : 'border-gray-100'
                                            }`}
                                        >
                                            {/* Package Header */}
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                                                    <img src={service.icon} alt="" className="w-6 h-6" />
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-gray-900">{service.name}</h3>
                                                    <p className="text-sm text-gray-500">{service.duration}</p>
                                                </div>
                                            </div>

                                            {/* Features */}
                                            <div className="space-y-2 mb-4 flex-1">
                                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                                    <span className="text-green-500">✓</span>
                                                    {service.units} {t('units') || 'бирлік'}
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                                    <span className="text-green-500">✓</span>
                                                    {service.price} {service.currency}
                                                </div>
                                            </div>

                                            {/* Price Display */}
                                            <div className="mb-4">
                                                <span className="text-2xl font-bold text-gray-900">{service.price}</span>
                                                <span className="text-gray-500 ml-1">{service.currency}</span>
                                            </div>

                                            {/* Warning if not enough balance */}
                                            {!canAfford && (
                                                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                                                    {t('notEnoughCredits') || 'Баланс жеткіліксіз'}
                                                </div>
                                            )}

                                            {/* Activate Button */}
                                            <button
                                                onClick={() => handleActivateClick(data.id, service)}
                                                disabled={hasActivePromotion || !canAfford || isLoadingPromotion}
                                                className={`w-full py-3 px-4 rounded-xl font-semibold transition-all ${
                                                    isActive
                                                        ? 'bg-green-500 text-white cursor-default'
                                                        : hasActivePromotion
                                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                        : !canAfford
                                                        ? 'bg-red-100 text-red-600 cursor-not-allowed'
                                                        : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 shadow-lg shadow-green-500/30'
                                                }`}
                                            >
                                                {isLoadingPromotion
                                                    ? t('loading') || 'Жүктелуде...'
                                                    : hasActivePromotion
                                                    ? t('activePromotionBlocked') || 'Белсенді промо бар'
                                                    : isActive
                                                    ? t('activated') || 'Белсенді'
                                                    : t('activate') || 'Белсендіру'}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
    );
};

export default PromoteContent;
